import {
  Autocomplete,
  TextField,
  CircularProgress,
  Box,
  Typography,
} from '@mui/material';

export interface DropdownOption {
  code: string;
  name: string;
  severity?: string | null;
}

interface SearchableDropdownProps {
  options: DropdownOption[];
  value: string | null;
  onChange: (value: string | null) => void;
  onBlur?: () => void;
  placeholder?: string;
  loading?: boolean;
  disabled?: boolean;
  error?: boolean;
}

export function SearchableDropdown({
  options,
  value,
  onChange,
  onBlur,
  placeholder = 'Chọn...',
  loading = false,
  disabled = false,
  error = false,
}: SearchableDropdownProps) {
  const selectedOption = options.find((opt) => opt.code === value) || null;

  const formatOptionLabel = (option: DropdownOption): string => {
    if (option.severity) {
      return `${option.name} (${option.code}) ${option.severity}`;
    }
    return `${option.name} (${option.code})`;
  };

  return (
    <Autocomplete
      options={options}
      value={selectedOption}
      openOnFocus
      onChange={(_, newValue) => {
        onChange(newValue ? newValue.code : null);
      }}
      onBlur={onBlur}
      getOptionLabel={formatOptionLabel}
      isOptionEqualToValue={(option, value) => option.code === value.code}
      loading={loading}
      disabled={disabled}
      noOptionsText="Không tìm thấy kết quả"
      ListboxProps={{
        sx: {
          maxHeight: 300,
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          error={error}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.code}>
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {option.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {option.code}
              {option.severity && ` • ${option.severity}`}
            </Typography>
          </Box>
        </Box>
      )}
    />
  );
}
